import { AutoCompleteReadonlyState } from '../components/CollectionViewsDropdown/AutoCompleteReadonlyState';
import { CollectionFilterState } from './CollectionFilterState';
import { DropdownSelectState } from './DropdownSelectState';
import { WixPatternsContainer, FiltersMap } from '@wix/bex-core';
import { action, computed, makeObservable } from 'mobx';
import { FilterToolbarState } from '../providers';

export interface DropdownCollectionFilterStateParams<
  V,
  F extends FiltersMap = FiltersMap,
> {
  collectionFilter: CollectionFilterState<V, F>;
  resetOnClose?: boolean;
  container: WixPatternsContainer;
  toolbar: FilterToolbarState;
}

export class DropdownCollectionFilterState<
  V,
  F extends FiltersMap = FiltersMap,
> {
  readonly container;
  readonly collectionFilter: DropdownCollectionFilterStateParams<
    V,
    F
  >['collectionFilter'];

  readonly dropdownSelect: DropdownSelectState<V>;
  readonly resetOnClose: DropdownCollectionFilterStateParams<
    V,
    F
  >['resetOnClose'];

  inputState;

  get shouldShowPlaceholder() {
    return (
      this.inputState.disableEditing ||
      this.collectionFilter.collection.query.search.isEmpty
    );
  }

  constructor(params: DropdownCollectionFilterStateParams<V, F>) {
    this.resetOnClose = params.resetOnClose;
    this.container = params.container;
    this.inputState = new AutoCompleteReadonlyState();

    this.collectionFilter = params.collectionFilter;

    const { collectionFilter } = this;

    const { select, filter } = collectionFilter;

    this.dropdownSelect = new DropdownSelectState<V>({
      select,
      container: params.container,
      toolbar: params.toolbar,
      filter,
    });

    makeObservable(this, {
      init: action.bound,
      shouldShowPlaceholder: computed,
    });
  }

  init({ skipInitialFetch }: { skipInitialFetch?: boolean } = {}) {
    const { dropdownSelect, resetOnClose, collectionFilter } = this;
    const { collection } = collectionFilter;

    const disposers = [
      dropdownSelect.init(),
      collection.init({
        skipInitialFetch,
      }),
    ];

    if (resetOnClose) {
      dropdownSelect.events.on('close', collection.resetFiltersAndRefresh);
    }

    return () => {
      for (const disposer of disposers) {
        disposer();
      }

      if (resetOnClose) {
        dropdownSelect.events.removeListener(
          'close',
          collection.resetFiltersAndRefresh,
        );
      }
    };
  }
}
